import { useQueryClient } from '@tanstack/react-query';
import { ComponentProps, useCallback, useContext, useState } from 'react';
import isEqual from 'react-fast-compare';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { getSidePanelSwitchToUrl } from 'src/components/Events/EventPage/utils';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Stack } from 'src/core/ui';
import { EmptyVendorAccountId } from 'src/dialogs/PurchaseVendorAccountDialog';
import { GET_ACCESSIBLE_VENDOR_ACCOUNTS_KEY } from 'src/hooks/api/useGetAccessibleVendorAccountsQuery';
import { ContentId } from 'src/utils/constants/contentId';
import { posChangedField } from 'src/utils/posFieldUtils';
import { validatePurchaseOrders } from 'src/utils/purchaseUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  ApiException,
  PartialPurchaseVendorAccount,
  PurchaseClient,
  PurchaseVendorAccount,
  PurchaseVendorAccountDetails,
} from 'src/WebApiController';

import { CancellableFormHeader } from '../common/CancellableFormHeader';
import { EventEntityHeader } from '../common/EventEntityHeader';
import { StyledPurchaseWizardBody } from '../common/Purchase';
import { PurchaseWizardFooter } from '../common/Purchase/PurchaseWizardFooter';
import { ModalBody } from '../Modal';
import { ModalBodyDataContainer } from '../Modal/Modal.styled';
import { PurchaseOrderFormDataType } from './MultiplePurchaseWizard';
import { useMultiplePurchaseWizardContext } from './MultiplePurchaseWizardContext';
import {
  MultiplePurchaseFlowConfig,
  Steps,
} from './MultiplePurchaseWizardFlow';

export function MultiplePurchaseWizardBody({
  formState,
  setError,
  clearErrors,
  getValues,
  setValue,
  control,
}: Omit<
  ComponentProps<typeof FormProvider<PurchaseOrderFormDataType, unknown>>,
  'children'
> & {
  purchaseHasMergedListings?: boolean | null;
  highlightTicketGroupIds?: number[];
}) {
  const { poValidators } = useMultiplePurchaseWizardContext();
  const { closeModal } = useContext(ModalContext);
  const requiredMsg = useContent(ContentId.Required);
  const orderIdUsedErrorMessage = useContent(ContentId.OrderIdInUseSameVendor);
  const { activeAccountWebClientConfig, loginContext, mainRoute } =
    useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState<keyof typeof Steps>(
    Steps.SearchEvent
  );

  const fieldArrayMethods = useFieldArray({
    control,
    name: 'purchaseOrders',
  });

  const stepConfig = MultiplePurchaseFlowConfig[currentStep];
  const { component: StepComponent, footerActions, title } = stepConfig;

  const onNextFromPurchaseWizardMainStep = () => {
    const { purchaseOrders } = getValues();
    const isPOValid = validatePurchaseOrders(
      requiredMsg,
      orderIdUsedErrorMessage,
      clearErrors,
      setError,
      purchaseOrders
    );

    const isTicketGroupsValid = poValidators.every((validator) => validator());

    return isPOValid && isTicketGroupsValid;
  };

  const onSavePartialVendorAccount = useCallback(
    async (partialVendorAccount: PartialPurchaseVendorAccount) => {
      return await tryInvokeApi(
        async () => {
          const vendorAccount = {
            id: EmptyVendorAccountId,
            vendorId: partialVendorAccount.vendorId,
            name: partialVendorAccount.name,
            email: partialVendorAccount.email,
          } as PurchaseVendorAccountDetails;
          const newId = await new PurchaseClient(
            activeAccountWebClientConfig
          ).mergeVendorAccount(vendorAccount);

          if (newId) {
            queryClient.invalidateQueries({
              queryKey: [
                GET_ACCESSIBLE_VENDOR_ACCOUNTS_KEY,
                activeAccountWebClientConfig.activeAccountId,
                partialVendorAccount.vendorId,
              ],
            });

            return {
              ...vendorAccount,
              id: newId,
            };
          } else {
            // This should never happen
            // Normal errors would have fallen to the error catch
            showErrorDialog(
              'PurchaseClient.mergeVendorAccount',
              {
                message:
                  'Unable to create or save vendor account ' + vendorAccount.id,
                status: 500,
              } as ApiException,
              {
                trackErrorData: vendorAccount,
              }
            );
          }
        },
        (error) => {
          showErrorDialog('PurchaseClient.mergeVendorAccount', error, {
            trackErrorData: partialVendorAccount,
          });
        }
      );
    },
    [activeAccountWebClientConfig, queryClient, showErrorDialog]
  );

  const onSubmitPurchaseOrder = useCallback(
    async (data: PurchaseOrderFormDataType) => {
      const { purchaseOrders } = data;
      await Promise.all(
        purchaseOrders.map(async (po, i) => {
          const { vendorAccount, partialVendorAccount } = po;
          if (!vendorAccount?.value && partialVendorAccount) {
            const newVendorAccount =
              await onSavePartialVendorAccount(partialVendorAccount);
            if (newVendorAccount) {
              const newVendorAccountChangedField = posChangedField(
                newVendorAccount as PurchaseVendorAccount
              );
              setValue(
                `purchaseOrders.${i}.vendorAccount`,
                newVendorAccountChangedField
              );
              setValue(`purchaseOrders.${i}.partialVendorAccount`, null);
              po.vendorAccount = newVendorAccountChangedField;
            }
          }
          po.partialVendorAccount = null;
        })
      );
      tryInvokeApi(
        async () => {
          setIsSubmitting(true);
          const client = new PurchaseClient(activeAccountWebClientConfig);

          const result = await client.insertPurchaseOrders(
            purchaseOrders,
            true // isManual
          );

          setIsSubmitting(false);

          const navRoute = getSidePanelSwitchToUrl({
            sectionType: SectionType.PurchaseSideTable,
            eventId: purchaseOrders[0].ticketGroups[0].viagogoEventId,
            hasFullPageEventViewEntitySelection: true,
          });
          navigate(navRoute);
        },
        (error) => {
          console.error('Error submitting purchase orders:', error);
        }
      );
    },
    []
  );

  return (
    <StyledPurchaseWizardBody>
      <CancellableFormHeader
        disabled={formState.isSubmitting}
        showDialogOnCancel={!isEqual(formState.defaultValues, getValues())}
        onClose={() => closeModal(true)}
      >
        <EventEntityHeader title={<Content id={title} />} />
      </CancellableFormHeader>
      <Stack
        direction="row"
        style={{ height: '100%', width: '100%', overflowX: 'auto' }}
      >
        <ModalBody>
          <ModalBodyDataContainer>
            <StepComponent {...fieldArrayMethods} />
          </ModalBodyDataContainer>
        </ModalBody>
      </Stack>
      {isSubmitting && <PosSpinner />}
      <PurchaseWizardFooter
        {...footerActions}
        isSubmitting={isSubmitting || fieldArrayMethods.fields.length === 0}
        onNext={() => {
          if (currentStep === Steps.PurchaseInput) {
            const isValid = onNextFromPurchaseWizardMainStep();
            if (!isValid) {
              return;
            }
          } else if (currentStep === Steps.Review) {
            onSubmitPurchaseOrder(getValues());
          }
          setCurrentStep(footerActions.nextStep);
        }}
        onPrevious={() => setCurrentStep(footerActions.prevStep)}
      />
    </StyledPurchaseWizardBody>
  );
}
